/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import {
  BuilderFormSchemaFactory,
  BuilderFormStateSchema,
  BuilderUserContext,
  FormRootSchema,
  FormState, FormStateSchema,
  whenCreateOrEditMode,
} from '@gh/builder-shared';
import {
  BuilderPermission,
  EMPTY_XERO_SYNC_PROGRESS,
  PRIVILEGED_EP_SYS_VARS_ACCOUNTS,
} from '@gh/builder-shared-data';
import { FormMode, setControlValue } from '@gh/core-ui';
import { BehaviorSubject } from 'rxjs';
import {
  getXeroSyncProgress,
  isXeroSyncInProgress,
  SystemVarsAccountsXero,
  SystemVarsAccountsXeroSyncProgress,
} from '../../../resources/model/system-vars-accounts.model';

export const privilegedFormFields: string[] = [
  'xeroClientId',
  'xeroClientSecret',
  'organizationName',
  'selectedFinancialYear',
];

@Injectable()
export class SystemVarsAccountsXeroFormState extends FormState<SystemVarsAccountsXero> {
  progress$ = new BehaviorSubject(EMPTY_XERO_SYNC_PROGRESS);

  constructor(
    schemaFactory: BuilderFormSchemaFactory,
    private userContext: BuilderUserContext,
  ) {
    super(schemaFactory);
  }

  get progress(): SystemVarsAccountsXeroSyncProgress {
    return this.progress$.value;
  }

  get isAnySyncInProgress(): boolean {
    return this.isSyncInProgress || this.isInitialSyncInProgress;
  }

  get isSyncInProgress(): boolean {
    return this.progress.syncInProgress;
  }

  get isInitialSyncInProgress(): boolean {
    return this.progress.initialSyncInProgress;
  }

  get isInitialSyncFailed(): boolean {
    return this.progress.initialSyncFailed;
  }

  get isInitialSyncSuccessed(): boolean {
    return !this.progress.initialSyncInProgress && !this.progress.initialSyncFailed;
  }

  build(settings: any): void {
    const progress = getXeroSyncProgress(settings);

    this.initSchema(settings);

    this.progress$.next(progress);
  }

  clearConnectionFields(): void {
      this.group.patchValue({
        organizationName: '',
        xeroClientId: '',
        xeroClientSecret: '',
        selectedFinancialYear: '',
      });
  }

  setSyncProgress(progress: SystemVarsAccountsXeroSyncProgress): void {
    this.progress$.next(progress);
    const inProgress = isXeroSyncInProgress(progress);
    if (inProgress && this.editable) {
      this.updateSchema({ mode: FormMode.ViewMode });
    } else if (!inProgress && this.viewMode) {
      this.build({ ...this.group.getRawValue(), progress });
      this.setFieldsPrivilegedEnabled(false);
    }
  }

  setFieldsPrivilegedEnabled(enabled: boolean): void {
    privilegedFormFields.forEach((field: string) => {
      this.get(field).updateSchema({
        enabled,
      });
    });
  }

  protected buildSchema(settings: any, { root }: FormStateSchema): FormRootSchema {
    const progress = getXeroSyncProgress(settings);
    const mode = this.userContext.hasPermission(BuilderPermission.Setup.SystemVariables.Accounts.EDIT)
    && !isXeroSyncInProgress(progress) ?
      FormMode.EditMode : FormMode.ViewMode;

    return root({
                  enabled: whenCreateOrEditMode(),
                  mode,
                  privilegedEntryPoints: [PRIVILEGED_EP_SYS_VARS_ACCOUNTS],
                  optimisticLock: true,
                  children: {
                    xeroWebhookKey: void 0,
                    xeroWebhookUrl: void 0,
                    xeroSyncType: void 0,
                    organizationName: {required: true},
                    xeroClientId: {required: true},
                    xeroClientSecret: {required: true},
                    xeroCallbackUrl: void 0,
                    etag: void 0,
                    selectedFinancialYear: void 0,
                  },
                });
  }
}
